import React, { useState, useEffect, useRef } from 'react';
import { OmniIconComponent } from '../../../../shared';
import AnimatedText from './AnimatedText';
import { getFieldDisplayName } from '../../services/placementUsageAnalytics';

/**
 * SmartSuggestionPanel Component
 * 
 * Side panel (30% width) displaying smart suggestions in AI chatbox style
 * Shows field-wise recent and frequent values with animations
 * 
 * @param {boolean} isOpen - Whether panel is open
 * @param {function} onClose - Close panel callback
 * @param {object} suggestionsData - Suggestions data from analytics service
 * @param {function} onAcceptRecent - Accept most recent values callback
 * @param {function} onAcceptFrequent - Accept most frequent values callback
 */
const SmartSuggestionPanel = ({ 
  isOpen, 
  onClose, 
  suggestionsData, 
  onAcceptRecent,
  onAcceptFrequent 
}) => {
  const [animationPhase, setAnimationPhase] = useState('intro'); // intro, fields, complete
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptingType, setAcceptingType] = useState(null); // 'recent' or 'frequent'
  const fieldAnimationTimeoutRef = useRef(null);
  const acceptTimeoutRef = useRef(null);
  
  // Get all field names from both mostRecent and mostFrequent (for team-wide mode)
  const fieldNames = suggestionsData ? [
    ...new Set([
      ...Object.keys(suggestionsData.mostRecent || {}),
      ...Object.keys(suggestionsData.mostFrequent || {})
    ])
  ] : [];
  
  // Reset animation when panel opens
  useEffect(() => {
    if (isOpen) {
      setAnimationPhase('intro');
      setCurrentFieldIndex(0);
      setIsAccepting(false);
      setAcceptingType(null);
    }
  }, [isOpen]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (fieldAnimationTimeoutRef.current) {
        clearTimeout(fieldAnimationTimeoutRef.current);
      }
      if (acceptTimeoutRef.current) {
        clearTimeout(acceptTimeoutRef.current);
      }
    };
  }, []);
  
  const handleIntroComplete = () => {
    setAnimationPhase('fields');
  };
  
  const handleFieldComplete = () => {
    if (currentFieldIndex < fieldNames.length - 1) {
      if (fieldAnimationTimeoutRef.current) {
        clearTimeout(fieldAnimationTimeoutRef.current);
      }
      fieldAnimationTimeoutRef.current = setTimeout(() => {
        setCurrentFieldIndex(prev => prev + 1);
      }, 100); // Small delay between fields
    } else {
      setAnimationPhase('complete');
    }
  };
  
  const handleAccept = (type) => {
    setIsAccepting(true);
    setAcceptingType(type);
    
    // Show 2-second animation
    if (acceptTimeoutRef.current) {
      clearTimeout(acceptTimeoutRef.current);
    }
    acceptTimeoutRef.current = setTimeout(() => {
      if (type === 'recent') {
        onAcceptRecent(suggestionsData.mostRecent);
      } else {
        onAcceptFrequent(suggestionsData.mostFrequent);
      }
      setIsAccepting(false);
      onClose();
    }, 2000);
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className={`smart-suggestion-backdrop ${isOpen ? 'open' : ''}`}
        onClick={onClose}
      />
      
      {/* Side Panel */}
      <div className={`smart-suggestion-panel ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="panel-header">
          <div className="header-content">
            <OmniIconComponent 
              className="is-size-2 ai-icon" 
              icon-id="omni:object:insights"
            />
            <h3>Smart Suggestions</h3>
          </div>
          <button 
            className="close-button icon medium"
            onClick={onClose}
            aria-label="Close"
          >
            <OmniIconComponent icon-id="omni:interactive:close" />
          </button>
        </div>
        
        {/* Content */}
        <div className="panel-content">
          {/* Intro Message */}
          {(animationPhase === 'intro' || animationPhase === 'fields' || animationPhase === 'complete') && (
            <div className="intro-message">
              <AnimatedText 
                text={`Based on ${suggestionsData?.metadata?.isUserSpecific ? 'your recent activity' : `${suggestionsData?.metadata?.placementType || 'this type of'} placement patterns`}, I've found patterns in ${suggestionsData?.metadata?.totalPlacements || 0} similar placements. Here are the most frequent${suggestionsData?.metadata?.isUserSpecific ? ' and recent' : ''} values:`}
                speed={40}
                onComplete={handleIntroComplete}
                instant={animationPhase !== 'intro'}
              />
            </div>
          )}
          
          {/* Field Suggestions */}
          {(animationPhase === 'fields' || animationPhase === 'complete') && (
            <div className="suggestions-list">
              {fieldNames.slice(0, currentFieldIndex + 1).map((fieldName, index) => {
                const recentData = suggestionsData.mostRecent?.[fieldName];
                const frequentData = suggestionsData.mostFrequent?.[fieldName];
                const isLastField = index === currentFieldIndex;
                
                return (
                  <div key={fieldName} className="suggestion-item">
                    <div className="field-name">
                      <AnimatedText 
                        text={getFieldDisplayName(fieldName)}
                        speed={30}
                        instant={!isLastField}
                        className="field-name-text"
                        onComplete={isLastField ? handleFieldComplete : undefined}
                      />
                    </div>
                    
                    {(animationPhase === 'complete' || !isLastField) && (
                      <div className="field-values">
                        {/* Most Recent */}
                        {recentData && (
                          <div className="value-card recent">
                            <div className="value-label">
                              <OmniIconComponent 
                                className="is-size-5" 
                                icon-id="omni:informative:history"
                              />
                              <span>Most Recent</span>
                            </div>
                            <div className="value-content">
                              <span className="value">{recentData.value}</span>
                              <span className="meta">
                                {recentData.daysAgo === 0 
                                  ? 'Today' 
                                  : recentData.daysAgo === 1 
                                    ? 'Yesterday'
                                    : `${recentData.daysAgo} days ago`
                                }
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Most Frequent */}
                        {frequentData && (
                          <div className="value-card frequent">
                            <div className="value-label">
                              <OmniIconComponent 
                                className="is-size-5" 
                                icon-id="omni:object:plan"
                              />
                              <span>Most Frequent</span>
                            </div>
                            <div className="value-content">
                              <span className="value">{frequentData.value}</span>
                              <span className="meta">
                                Used {frequentData.count} times ({frequentData.percentage}%)
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Footer Actions */}
        {animationPhase === 'complete' && !isAccepting && (
          <div className="panel-footer">
            {suggestionsData?.metadata?.isUserSpecific && (
              <button 
                className="primary"
                onClick={() => handleAccept('recent')}
              >
                <OmniIconComponent 
                  className="is-size-5" 
                  icon-id="omni:informative:history"
                />
                Accept Last Used
              </button>
            )}
            
            <button 
              className="secondary"
              onClick={() => handleAccept('frequent')}
            >
              <OmniIconComponent 
                className="is-size-5" 
                icon-id="omni:object:plan"
              />
              Accept Most Frequent
            </button>
            
            <button 
              className="tertiary"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        )}
        
        {/* Accepting Animation */}
        {isAccepting && (
          <div className="accepting-overlay">
            <p className="accepting-message">
              Applying {acceptingType === 'recent' ? 'most recent' : 'most frequent'} values...
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default SmartSuggestionPanel;
